/**
 * AirlineSelector - Header dropdown for switching between airlines
 *
 * Features:
 * - Shows current airline with icon
 * - Dropdown to switch to other airlines
 * - Displays role badge for each airline
 * - Visual indicator for default airline
 */

import React from 'react';
import { ChevronDown, Check, Globe, Building } from 'lucide-react';
import { useAirline, UserAllowedAirline } from '../contexts/AirlineContext';

// =====================================================================
// Component
// =====================================================================

export const AirlineSelector: React.FC = () => {
  const { selectedAirline, sessionAirlines, setSelectedAirline, isLoading } = useAirline();
  const isSingleAirline = sessionAirlines.length <= 1;
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ===================================================================
  // Handlers
  // ===================================================================

  const handleAirlineSelect = (airline: UserAllowedAirline) => {
    setSelectedAirline(airline);
    setIsOpen(false);
  };

  // ===================================================================
  // Render Helpers
  // ===================================================================

  const getAirlineIcon = (code: string, size: 'sm' | 'md' = 'md') => {
    const sizeNum = size === 'sm' ? 16 : 20;
    const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
    if (code === 'GLOBAL') {
      return <Globe size={sizeNum} className={`${sizeClass} text-sky-600`} />;
    }
    return <Building size={sizeNum} className={`${sizeClass} text-navy`} />;
  };

  const getRoleBadge = (role?: string) => {
    if (!role) return null;

    const colors = {
      admin: 'bg-red-100 text-red-800',
      editor: 'bg-blue-100 text-blue-800',
      viewer: 'bg-gray-100 text-gray-800',
    };

    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded ${colors[role as keyof typeof colors]}`}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  // ===================================================================
  // Loading State
  // ===================================================================

  if (isLoading || !selectedAirline) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-white border-2 border-navy/20 rounded-lg animate-pulse shadow-sm">
        <div className="w-4 h-4 bg-navy/20 rounded" />
        <div className="w-32 h-4 bg-navy/20 rounded" />
      </div>
    );
  }

  // ===================================================================
  // Render
  // ===================================================================

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected Airline Button - Compact */}
      <button
        onClick={() => !isSingleAirline && setIsOpen(!isOpen)}
        disabled={isSingleAirline}
        className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-navy bg-white border-2 border-navy/20 rounded-lg transition-all shadow-sm disabled:opacity-60 disabled:cursor-default hover:enabled:bg-navy/5 hover:enabled:border-navy/40 focus:enabled:outline-none focus:enabled:ring-2 focus:enabled:ring-navy/30"
        aria-label="Select airline"
        aria-expanded={isSingleAirline ? undefined : isOpen}
        aria-disabled={isSingleAirline}
      >
        {getAirlineIcon(selectedAirline.code, 'sm')}
        <span className="max-w-[160px] truncate">{selectedAirline.name}</span>
        {getRoleBadge(selectedAirline.role)}
        <ChevronDown
          size={16}
          className={`w-4 h-4 transition-transform ${isSingleAirline ? 'text-navy/30' : `text-navy/60 ${isOpen ? 'rotate-180' : ''}`}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2">
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Your Airlines
            </div>

            {sessionAirlines.map((airline) => {
              const isSelected = airline.airline_id === selectedAirline.airline_id;
              const isDefault = airline.is_default;

              return (
                <button
                  key={airline.airline_id}
                  onClick={() => handleAirlineSelect(airline)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                    isSelected
                      ? 'bg-sky-50 text-sky-900'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    {getAirlineIcon(airline.code)}
                  </div>

                  {/* Airline Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{airline.name}</span>
                      {isDefault && (
                        <span className="text-xs text-gray-500">(Default)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-500">{airline.code}</span>
                      {getRoleBadge(airline.role)}
                    </div>
                  </div>

                  {/* Selected Indicator */}
                  {isSelected && (
                    <Check size={20} className="w-5 h-5 text-sky-600 flex-shrink-0" />
                  )}
                </button>
              );
            })}

            {sessionAirlines.length === 0 && (
              <div className="px-3 py-8 text-center text-sm text-gray-500">
                No airlines available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================================
// Compact Version (for mobile/narrow spaces)
// =====================================================================

export const AirlineSelectorCompact: React.FC = () => {
  const { selectedAirline, sessionAirlines, setSelectedAirline } = useAirline();
  const [isOpen, setIsOpen] = React.useState(false);

  if (!selectedAirline) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
      >
        <span className="font-medium">{selectedAirline.code}</span>
        <ChevronDown size={16} className="w-4 h-4" />
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg">
          {sessionAirlines.map((airline) => (
            <button
              key={airline.airline_id}
              onClick={() => {
                setSelectedAirline(airline);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                airline.airline_id === selectedAirline.airline_id
                  ? 'bg-sky-50 text-sky-900 font-medium'
                  : 'text-gray-700'
              }`}
            >
              {airline.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
